import React, { useState, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { getPeopleCountingDataService } from "services/api-service";

const css = `
.dash-root { padding: 0 0 60px; font-family: 'Sora', sans-serif; background: #f4f7fe; min-height: 100vh; }
.dash-body { max-width: 1440px; margin: 0 auto; padding: 28px 36px 0; }

/* Filter Pills Bar */
.filter-bar {
  display: flex; align-items: center; justify-content: space-between;
  background: #ffffff; border: 1px solid #e2e8f0; border-radius: 16px;
  padding: 10px 16px; margin-bottom: 24px; box-shadow: 0 2px 4px rgba(0,0,0,0.02);
  flex-wrap: wrap; gap: 12px;
}
.filter-title { font-size: 13px; font-weight: 700; color: #475569; display: flex; align-items: center; gap: 6px; }
.filter-pills { display: flex; gap: 8px; flex-wrap: wrap; }
.filter-pill {
  padding: 8px 16px; border-radius: 10px; font-size: 12px; font-weight: 700;
  border: 1px solid #e2e8f0; background: #f8fafc; color: #64748b;
  cursor: pointer; transition: all 0.2s; font-family: 'Sora', sans-serif;
}
.filter-pill:hover { background: #eff6ff; color: #2563eb; border-color: #bfdbfe; }
.filter-pill.active { background: #2563eb; color: #ffffff; border-color: #2563eb; box-shadow: 0 4px 10px rgba(37,99,235,0.25); }

/* Stats Grid */
.dash-stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; margin-bottom: 28px; }
@media (max-width: 900px) { .dash-stats-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 480px) { .dash-stats-grid { grid-template-columns: 1fr; } }

.modern-stat-card { 
  background: #ffffff; border: 1px solid #e2e8f0; border-radius: 20px; 
  padding: 24px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.03); 
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s; 
  position: relative; overflow: hidden;
}
.modern-stat-card:hover { transform: translateY(-4px); box-shadow: 0 12px 20px -3px rgba(0,0,0,0.08); }
.modern-stat-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 4px; background: var(--accent-gradient); }

.stat-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 16px; }
.stat-icon { width: 48px; height: 48px; border-radius: 14px; display: flex; align-items: center; justify-content: center; font-size: 24px; background: var(--icon-bg); color: var(--icon-color); }
.stat-title { font-size: 12px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 4px;}
.stat-value { font-size: 38px; font-weight: 800; color: #0f172a; font-family: 'JetBrains Mono', monospace; line-height: 1; margin-bottom: 8px; letter-spacing: -1px;}
.stat-sub { font-size: 12px; font-weight: 500; color: #94a3b8; display: flex; align-items: center; gap: 4px;}
.stat-highlight { color: var(--icon-color); font-weight: 700; background: var(--icon-bg); padding: 2px 6px; border-radius: 4px;}

/* Chart Container */
.chart-container { 
  background: #ffffff; border-radius: 20px; padding: 30px; 
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.03); border: 1px solid #e2e8f0; 
  margin-bottom: 24px;
}
.chart-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 30px; }
.chart-title { font-size: 18px; font-weight: 800; color: #0f172a; letter-spacing: -0.3px;}
.chart-subtitle { font-size: 13px; color: #64748b; font-weight: 500; margin-top: 4px; }

/* Modern Bar Chart */
.hourly-chart { display: flex; align-items: flex-end; justify-content: space-between; height: 280px; padding-top: 30px; border-bottom: 2px solid #f1f5f9; gap: 4px;}
.bar-col { flex: 1; display: flex; flex-direction: column; justify-content: flex-end; align-items: center; position: relative; height: 100%; border-radius: 8px; transition: background 0.2s; cursor: pointer; padding: 0 2px;}
.bar-col:hover { background: #f8fafc; }
.bar-fill { 
  width: 100%; max-width: 42px; 
  background: linear-gradient(180deg, #3b82f6 0%, #1d4ed8 100%); 
  border-radius: 8px 8px 0 0; transition: height 0.6s cubic-bezier(0.4, 0, 0.2, 1); 
  box-shadow: 0 4px 12px rgba(37,99,235,0.15); 
}
.bar-value { 
  font-size: 11px; font-weight: 800; color: #64748b; margin-bottom: 8px; 
  font-family: 'JetBrains Mono', monospace; opacity: 0; transition: all 0.2s; transform: translateY(10px);
}
.bar-col:hover .bar-value { opacity: 1; color: #1d4ed8; transform: translateY(0); }
.hour-label { font-size: 11px; font-weight: 600; color: #94a3b8; margin-top: 12px; font-family: 'JetBrains Mono', monospace; white-space: nowrap;}

/* Breakdown Panels (Morning/Afternoon/Evening) */
.breakdown-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin-top: 24px; }
@media (max-width: 768px) { .breakdown-grid { grid-template-columns: 1fr; } }
.breakdown-card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 16px; padding: 20px; display: flex; align-items: center; gap: 16px;}
.breakdown-icon { font-size: 32px; }
.breakdown-info { flex: 1; }
.breakdown-title { font-size: 12px; font-weight: 700; color: #64748b; text-transform: uppercase; margin-bottom: 4px;}
.breakdown-value { font-size: 24px; font-weight: 800; color: #0f172a; font-family: 'JetBrains Mono', monospace; }
.breakdown-bar-bg { height: 6px; background: #e2e8f0; border-radius: 10px; margin-top: 8px; overflow: hidden;}
.breakdown-bar-fill { height: 100%; border-radius: 10px; }

.dash-spinner-row { display: flex; flex-direction: column; align-items: center; justify-content: center; color: #94a3b8; font-size: 14px; font-weight: 600; height: 100%; gap: 12px;}
.dash-spinner { width: 32px; height: 32px; border: 3px solid rgba(0,0,0,0.1); border-top-color: #3b82f6; border-radius: 50%; animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
`;

const ModernStatCard = ({ icon, iconBg, iconColor, gradient, title, value, subText, highlight }) => (
  <div className="modern-stat-card" style={{ "--accent-gradient": gradient, "--icon-bg": iconBg, "--icon-color": iconColor }}>
    <div className="stat-header">
      <div className="stat-title">{title}</div>
      <div className="stat-icon">{icon}</div>
    </div>
    <div className="stat-value">{value}</div>
    <div className="stat-sub">
      {highlight && <span className="stat-highlight">{highlight}</span>}
      {subText}
    </div>
  </div>
);

const PeopleCountingBoard = ({ selectedDate }) => {
  const [loading, setLoading] = useState(false);
  const [analyticsData, setAnalyticsData] = useState([]);
  const [breakdown, setBreakdown] = useState({ morning: 0, afternoon: 0, evening: 0 });
  const [viewType, setViewType] = useState("hourly");
  const [filterType, setFilterType] = useState("selected"); // 'selected', '3days', '7days', '30days'
  const [rangeDates, setRangeDates] = useState({ start: selectedDate, end: selectedDate });

  // Calculate start and end dates based on filter preset
  useEffect(() => {
    if (!selectedDate) return;
    
    const end = new Date(selectedDate);
    let start = new Date(selectedDate);

    if (filterType === "3days") {
      start.setDate(end.getDate() - 2);
    } else if (filterType === "7days") {
      start.setDate(end.getDate() - 6);
    } else if (filterType === "30days") {
      start.setDate(end.getDate() - 29);
    }

    const formatDateStr = (d) => d.toISOString().split('T')[0];
    const sStr = formatDateStr(start);
    const eStr = formatDateStr(end);

    setRangeDates({ start: sStr, end: eStr });

    setLoading(true);
    getPeopleCountingDataService({ start_date: sStr, end_date: eStr })
      .then(res => {
        setAnalyticsData(res.data.data || []);
        setViewType(res.data.view_type || "hourly");
        setBreakdown(res.data.breakdown || { morning: 0, afternoon: 0, evening: 0 });
      })
      .catch(err => {
        console.error("Failed to fetch counting analytics", err);
        toast.error("Failed to load analytics");
        setAnalyticsData([]);
        setBreakdown({ morning: 0, afternoon: 0, evening: 0 });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [selectedDate, filterType]);

  /* ── Deep Analytics Calculations ── */
  const analytics = useMemo(() => {
    if (!analyticsData.length) return { 
      totalFootfall: 0, peakLabel: 'N/A', peakVal: 0, avgPerUnit: 0, maxBar: 1
    };
    
    let totalFootfall = 0;
    let peakVal = 0;
    let peakLabel = 'N/A';

    analyticsData.forEach(d => {
      totalFootfall += d.entered;
      if (d.entered > peakVal) {
        peakVal = d.entered;
        peakLabel = d.label;
      }
    });

    const avgPerUnit = Math.round(totalFootfall / analyticsData.length);

    return {
      totalFootfall,
      peakLabel,
      peakVal,
      avgPerUnit,
      maxBar: peakVal || 1
    };
  }, [analyticsData]);

  // Use the backend-provided breakdown for percentages
  const mornPct = analytics.totalFootfall ? (breakdown.morning / analytics.totalFootfall) * 100 : 0;
  const aftPct  = analytics.totalFootfall ? (breakdown.afternoon / analytics.totalFootfall) * 100 : 0;
  const evePct  = analytics.totalFootfall ? (breakdown.evening / analytics.totalFootfall) * 100 : 0;

  return (
    <>
      <style>{css}</style>
      <div className="dash-root">
        <div className="dash-body">
          
          {/* Quick Preset Filter Bar */}
          <div className="filter-bar">
            <div className="filter-title">
              📊 Analytics Range: <span style={{ color: '#2563eb' }}>{rangeDates.start === rangeDates.end ? rangeDates.start : `${rangeDates.start} to ${rangeDates.end}`}</span>
            </div>
            <div className="filter-pills">
              <button 
                className={`filter-pill ${filterType === "selected" ? "active" : ""}`} 
                onClick={() => setFilterType("selected")}
              >
                📅 Selected Date ({selectedDate})
              </button>
              <button 
                className={`filter-pill ${filterType === "3days" ? "active" : ""}`} 
                onClick={() => setFilterType("3days")}
              >
                ⚡ Last 3 Days
              </button>
              <button 
                className={`filter-pill ${filterType === "7days" ? "active" : ""}`} 
                onClick={() => setFilterType("7days")}
              >
                📆 Last 7 Days (Week)
              </button>
              <button 
                className={`filter-pill ${filterType === "30days" ? "active" : ""}`} 
                onClick={() => setFilterType("30days")}
              >
                📈 Last 30 Days (Month)
              </button>
            </div>
          </div>

          {/* Top Summary Cards */}
          <div className="dash-stats-grid">
            <ModernStatCard 
              title="Total Footfall" 
              value={loading ? "..." : analytics.totalFootfall} 
              icon="🚶‍♂️" iconBg="#eff6ff" iconColor="#3b82f6" gradient="linear-gradient(90deg, #3b82f6, #60a5fa)"
              subText={viewType === "hourly" ? "Total visits today" : "Total visits in selected period"}
            />
            <ModernStatCard 
              title={viewType === "hourly" ? "Peak Hour" : "Busiest Day"} 
              value={loading ? "..." : analytics.peakLabel} 
              icon="🔥" iconBg="#fffbeb" iconColor="#f59e0b" gradient="linear-gradient(90deg, #f59e0b, #fbbf24)"
              highlight={analytics.peakVal}
              subText=" visitors recorded"
            />
            <ModernStatCard 
              title={viewType === "hourly" ? "Avg. Hourly Visitors" : "Avg. Daily Visitors"} 
              value={loading ? "..." : analytics.avgPerUnit} 
              icon="📊" iconBg="#f5f3ff" iconColor="#8b5cf6" gradient="linear-gradient(90deg, #8b5cf6, #a78bfa)"
              subText={viewType === "hourly" ? "Average visits per active hour" : "Average visits per day"}
            />
            <ModernStatCard 
              title="Filter Mode" 
              value={filterType === "selected" ? "Single Day" : filterType === "3days" ? "3 Days" : filterType === "7days" ? "7 Days" : "30 Days"} 
              icon="⚙️" iconBg="#ecfeff" iconColor="#06b6d4" gradient="linear-gradient(90deg, #06b6d4, #22d3ee)"
              subText={`Grouped by ${viewType === "hourly" ? "Hours" : "Days"}`}
            />
          </div>

          {/* Main Chart Section */}
          <div className="chart-container">
            <div className="chart-header">
              <div>
                <div className="chart-title">
                  {viewType === "hourly" ? "Hourly Traffic Distribution" : "Daily Customer Footfall Trend"}
                </div>
                <div className="chart-subtitle">
                  Showing people entry traffic from {rangeDates.start} to {rangeDates.end}
                </div>
              </div>
            </div>

            {loading ? (
              <div className="dash-spinner-row" style={{ height: '280px' }}>
                <div className="dash-spinner" />
                <span>Aggregating analytics data...</span>
              </div>
            ) : analyticsData.length === 0 ? (
               <div style={{ height: '280px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '15px', fontWeight: '600' }}>
                 No traffic recorded for this selected range.
               </div>
            ) : (
              <>
                <div className="hourly-chart">
                  {analyticsData.map((d, i) => {
                    const heightPct = Math.max((d.entered / analytics.maxBar) * 100, 2);
                    return (
                      <div key={i} className="bar-col">
                        <div className="bar-value">{d.entered}</div>
                        <div className="bar-fill" style={{ height: `${heightPct}%` }} />
                        <div className="hour-label">{d.label}</div>
                      </div>
                    );
                  })}
                </div>

                {/* 👇 Breakdown Panels (Now visible in ALL modes) 👇 */}
                <div className="breakdown-grid">
                  <div className="breakdown-card">
                    <div className="breakdown-icon">🌅</div>
                    <div className="breakdown-info">
                      <div className="breakdown-title">Morning (6 AM - 12 PM)</div>
                      <div className="breakdown-value">{breakdown.morning} <span style={{fontSize:12, color:"#94a3b8", fontWeight:600}}>visitors</span></div>
                      <div className="breakdown-bar-bg">
                        <div className="breakdown-bar-fill" style={{ width: `${mornPct}%`, background: '#f59e0b' }}/>
                      </div>
                    </div>
                  </div>
                  
                  <div className="breakdown-card">
                    <div className="breakdown-icon">☀️</div>
                    <div className="breakdown-info">
                      <div className="breakdown-title">Afternoon (12 PM - 5 PM)</div>
                      <div className="breakdown-value">{breakdown.afternoon} <span style={{fontSize:12, color:"#94a3b8", fontWeight:600}}>visitors</span></div>
                      <div className="breakdown-bar-bg">
                        <div className="breakdown-bar-fill" style={{ width: `${aftPct}%`, background: '#3b82f6' }}/>
                      </div>
                    </div>
                  </div>

                  <div className="breakdown-card">
                    <div className="breakdown-icon">🌙</div>
                    <div className="breakdown-info">
                      <div className="breakdown-title">Evening (5 PM onwards)</div>
                      <div className="breakdown-value">{breakdown.evening} <span style={{fontSize:12, color:"#94a3b8", fontWeight:600}}>visitors</span></div>
                      <div className="breakdown-bar-bg">
                        <div className="breakdown-bar-fill" style={{ width: `${evePct}%`, background: '#8b5cf6' }}/>
                      </div>
                    </div>
                  </div>
                </div>
                {/* 👆 End Breakdown Panels 👆 */}
              </>
            )}
          </div>
          
        </div>
      </div>
    </>
  );
};

export default PeopleCountingBoard;
